<template>
  <div v-if="ready" class="feature">
    <FeatureMeta :feature="featureStore.feature" />

    <div v-if="featureStore.images.length === 0">
      <FeatureInitSection include-uploader :feature="featureStore.feature" />
      <PageCropImage />
    </div>

    <div v-else class="h-full">
      <LayoutDefault @finished="onFinished">
        <div class="max-w-3xl mx-auto">
          <img
            id="image"
            :src="getImageURL(featureStore.images[0])"
            :alt="featureStore.images[0].filename"
            class="shadow-2xl w-full max-w-full"
          />
        </div>

        <template #aside>
          <form class="flex flex-col space-y-6 mt-8" @change="handleOptions">
            <div class="flex items-center justify-between">
              <label for="cropWidth" class="input-label">Width (px)</label>
              <input
                id="cropWidth"
                v-model="featureStore.options[slug].cropWidth"
                min="1"
                type="number"
                class="input-field w-32 text-right auto-select-when-focused"
                placeholder="1024"
              />
            </div>
            <div class="flex items-center justify-between">
              <label for="cropHeight" class="input-label">Height (px)</label>
              <input
                id="cropHeight"
                v-model="featureStore.options[slug].cropHeight"
                min="1"
                type="number"
                class="input-field w-32 text-right auto-select-when-focused"
                placeholder="512"
              />
            </div>
            <div class="flex items-center justify-between">
              <label for="positionX" class="input-label">Position X</label>
              <input
                id="positionX"
                v-model="featureStore.options[slug].positionX"
                min="0"
                type="number"
                class="input-field w-32 text-right auto-select-when-focused"
                placeholder="0"
              />
            </div>
            <div class="flex items-center justify-between">
              <label for="positionY" class="input-label">Position Y</label>
              <input
                id="positionY"
                v-model="featureStore.options[slug].positionY"
                min="0"
                type="number"
                class="input-field w-32 text-right auto-select-when-focused"
                placeholder="0"
              />
            </div>

            <div class="flex flex-col space-y-4">
              <label class="inline-flex items-center ml-2 mb-5 cursor-pointer max-w-max">
                <input v-model="featureStore.options[slug].lockRatio" type="checkbox" class="sr-only peer" />
                <div
                  class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:w-5 after:h-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"
                ></div>
                <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Lock ratio</span>
              </label>
            </div>

            <div
              class="flex items-center p-2 border border-gray-200 rounded hover:bg-blue-100 cursor-pointer shadow-sm"
              :class="isYouTubeThumbnail ? 'bg-sky-200' : 'bg-zinc-50'"
              @click="setYouTubeThumbnail"
            >
              <i class="fa-brands fa-youtube"></i>
              <span class="ml-1">YouTube thumbnail (16:9)</span>
            </div>
          </form>

          <AppNotice v-if="!featureStore.options[slug].lockRatio" class="mt-12 ml-4"
            >Hold the Shift key while resizing to lock the aspect ratio</AppNotice
          >
        </template>
      </LayoutDefault>
    </div>
  </div>

  <div v-else class="feature-loader">
    <img src="@/images/system/loader.gif" alt="Loader" />
  </div>
</template>

<script setup lang="ts">
  import { onMounted, watch, computed } from 'vue'
  import { useFeatureSetup } from '$/composables/useFeatureSetup'
  import { getImageURL } from '$/utils'
  import PageCropImage from '&/landing_pages/PageCropImage.vue'
  import Cropper from 'cropperjs'
  import FeatureMeta from '&/FeatureMeta.vue'

  let cropper: InstanceType<typeof Cropper> | null = null
  let clamping = false

  const { ready, router, featureStore, slug, LayoutDefault, FeatureInitSection, AppNotice } = useFeatureSetup()

  onMounted(() => {
    if (featureStore.images.length > 0) setupCropper()
  })

  watch(
    () => featureStore.images,
    (newImages) => {
      if (newImages[0]) setupCropper()
    },
    { deep: true },
  )

  watch(
    () => featureStore.options[slug].lockRatio,
    (val) => {
      if (!cropper) return
      const selection = cropper.getCropperSelection()
      if (!selection) return

      if (val) {
        const w = featureStore.options[slug].cropWidth
        const h = featureStore.options[slug].cropHeight
        if (w && h) {
          selection.aspectRatio = w / h
        }
      } else {
        selection.aspectRatio = NaN
      }
    },
  )

  function onFinished() {
    router.push({ name: 'CropImageResult' })
  }

  // Convert canvas display coords → original image pixel coords
  function canvasToImage(cx: number, cy: number, cw: number, ch: number, img: HTMLImageElement) {
    if (!cropper) return null
    const cropperImage = cropper.getCropperImage()
    if (!cropperImage) return null
    const [a, , , d, e, f] = cropperImage.$getTransform()
    const nW = img.naturalWidth
    const nH = img.naturalHeight
    return {
      x: Math.round((cx - e - nW / 2) / a + nW / 2),
      y: Math.round((cy - f - nH / 2) / d + nH / 2),
      width: Math.round(cw / a),
      height: Math.round(ch / d),
    }
  }

  // Convert original image pixel coords → canvas display coords
  function imageToCanvas(ix: number, iy: number, iw: number, ih: number, img: HTMLImageElement) {
    if (!cropper) return null
    const cropperImage = cropper.getCropperImage()
    if (!cropperImage) return null
    const [a, , , d, e, f] = cropperImage.$getTransform()
    const nW = img.naturalWidth
    const nH = img.naturalHeight
    return {
      x: a * (ix - nW / 2) + e + nW / 2,
      y: d * (iy - nH / 2) + f + nH / 2,
      width: iw * a,
      height: ih * d,
    }
  }

  function handleOptions() {
    if (!cropper) return
    const imageElement = document.getElementById('image') as HTMLImageElement | null
    const selection = cropper.getCropperSelection()
    if (!selection || !imageElement) return
    const canvas = imageToCanvas(
      Number(featureStore.options[slug].positionX),
      Number(featureStore.options[slug].positionY),
      Number(featureStore.options[slug].cropWidth),
      Number(featureStore.options[slug].cropHeight),
      imageElement,
    )
    if (canvas) selection.$change(canvas.x, canvas.y, canvas.width, canvas.height)
  }

  function setupCropper() {
    setTimeout(() => {
      const imageElement = document.getElementById('image') as HTMLImageElement | null
      if (!imageElement) return
      if (cropper) cropper.destroy()
      cropper = new Cropper(imageElement, {
        template: `<cropper-canvas background scale-step="0">
          <cropper-image scalable translatable></cropper-image>
          <cropper-shade hidden></cropper-shade>
          <cropper-handle action="select" plain></cropper-handle>
          <cropper-selection initial-coverage="0.5" movable resizable>
            <cropper-grid role="grid" bordered covered></cropper-grid>
            <cropper-crosshair centered></cropper-crosshair>
            <cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>
            <cropper-handle action="n-resize"></cropper-handle>
            <cropper-handle action="e-resize"></cropper-handle>
            <cropper-handle action="s-resize"></cropper-handle>
            <cropper-handle action="w-resize"></cropper-handle>
            <cropper-handle action="ne-resize"></cropper-handle>
            <cropper-handle action="nw-resize"></cropper-handle>
            <cropper-handle action="se-resize"></cropper-handle>
            <cropper-handle action="sw-resize"></cropper-handle>
          </cropper-selection>
        </cropper-canvas>`,
      })

      const cropperCanvas = cropper.getCropperCanvas()
      if (cropperCanvas && imageElement.naturalWidth) {
        const aspectRatio = imageElement.naturalHeight / imageElement.naturalWidth
        cropperCanvas.style.height = `${cropperCanvas.offsetWidth * aspectRatio}px`
      }

      const selection = cropper.getCropperSelection()
      if (selection) {
        selection.addEventListener('change', (event: Event) => {
          if (clamping) return
          let { x, y, width, height } = (event as CustomEvent).detail
          const canvas = cropper!.getCropperCanvas()
          if (canvas) {
            const maxX = canvas.offsetWidth
            const maxY = canvas.offsetHeight
            const cx = Math.max(0, Math.min(x, maxX - width))
            const cy = Math.max(0, Math.min(y, maxY - height))
            const cw = Math.min(width, maxX)
            const ch = Math.min(height, maxY)
            if (cx !== x || cy !== y || cw !== width || ch !== height) {
              event.preventDefault()
              clamping = true
              selection.$change(cx, cy, cw, ch)
              clamping = false
              x = cx
              y = cy
              width = cw
              height = ch
            }
          }
          const img = canvasToImage(x, y, width, height, imageElement)
          if (!img) return
          featureStore.options[slug].cropWidth = img.width
          featureStore.options[slug].cropHeight = img.height
          featureStore.options[slug].positionX = img.x
          featureStore.options[slug].positionY = img.y
        })
      }
    }, 500)
  }

  const isYouTubeThumbnail = computed(() => {
    return Math.abs(featureStore.options[slug].cropWidth * 9 - featureStore.options[slug].cropHeight * 16) < 20
  })
  function setYouTubeThumbnail() {
    if (!cropper) return
    const selection = cropper.getCropperSelection()
    if (selection) selection.aspectRatio = 16 / 9
    handleOptions()
    featureStore.options[slug].lockRatio = true
  }
</script>
